import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/shared/SEO';
import { useI18n } from '../i18n/useI18n';

const COMPANY = {
  name: 'PT. Rekayasa Analisa Digital',
  address: 'Jalan Margonda Raya No.328 RT.00 RW.00, Kemiri Muka, Beji, Kota Depok, Jawa Barat',
  email: 'sales@rekayasa.io',
};

const lastUpdatedISO = '2026-02-04';

const formatDate = (lang: 'en' | 'id', iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat(lang === 'id' ? 'id-ID' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d);
};

export default function TermsPage() {
  const { lang } = useI18n();

  const title = lang === 'id' ? 'Syarat & Ketentuan' : 'Terms & Conditions';
  const description =
    lang === 'id'
      ? 'Syarat dan ketentuan penggunaan situs Rekayasa / ERICA.'
      : 'Terms and conditions for using the Rekayasa / ERICA website.';

  return (
    <div className="bg-white">
      <SEO title={`${title} | ERICA by Rekayasa`} description={description} lang={lang} />

      <div className="mx-auto max-w-3xl px-4 pb-20 pt-28 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {title}
          </h1>
          <Link to="/" className="text-sm font-semibold text-slate-700 hover:text-slate-900">
            {lang === 'id' ? 'Kembali' : 'Back'}
          </Link>
        </div>

        <p className="mt-4 text-sm text-slate-600">
          {lang === 'id' ? 'Terakhir diperbarui: ' : 'Last updated: '}
          <span className="font-medium text-slate-800">{formatDate(lang, lastUpdatedISO)}</span>
        </p>

        <div className="mt-10 space-y-10 text-slate-700">
          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Ringkasan' : 'Summary'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Dengan mengakses situs ini, Anda menyetujui syarat dan ketentuan berikut. Jika Anda tidak setuju, mohon tidak menggunakan situs ini.'
                : 'By accessing this website, you agree to these terms. If you do not agree, please do not use the website.'}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Ini bukan nasihat hukum. Untuk kebutuhan enterprise, kami sarankan review bersama tim legal Anda.'
                : 'This is not legal advice. For enterprise needs, we recommend a review with your legal team.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Penggunaan situs' : 'Use of the website'}
            </h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-slate-600">
              <li>
                {lang === 'id'
                  ? 'Anda boleh menggunakan situs ini untuk mempelajari ERICA dan mengajukan permintaan demo.'
                  : 'You may use this website to learn about ERICA and request a demo.'}
              </li>
              <li>
                {lang === 'id'
                  ? 'Dilarang mencoba mengganggu, merusak, atau menyalahgunakan situs.'
                  : 'You must not attempt to disrupt, damage, or misuse the website.'}
              </li>
              <li>
                {lang === 'id'
                  ? 'Konten dapat berubah sewaktu-waktu tanpa pemberitahuan.'
                  : 'Content may change at any time without notice.'}
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Permintaan demo' : 'Demo requests'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Permintaan demo yang Anda kirim akan kami tindaklanjuti melalui email. Kami berhak menolak atau menjadwalkan ulang demo sesuai ketersediaan dan kebijakan internal.'
                : 'Demo requests will be followed up via email. We may decline or reschedule requests based on availability and internal policy.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Hak kekayaan intelektual' : 'Intellectual property'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Seluruh konten pada situs (teks, visual, merek, dan materi lainnya) adalah milik PT. Rekayasa Analisa Digital atau pemberi lisensinya, kecuali dinyatakan lain.'
                : 'All content on this website (text, visuals, branding, and other materials) is owned by PT. Rekayasa Analisa Digital or its licensors unless stated otherwise.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Tautan pihak ketiga' : 'Third-party links'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Situs ini dapat memuat tautan ke situs pihak ketiga (mis. ERICA Dashboard). Kami tidak bertanggung jawab atas konten atau kebijakan situs pihak ketiga.'
                : 'This website may link to third-party sites (e.g., ERICA Dashboard). We are not responsible for third-party content or policies.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Penyangkalan & batasan tanggung jawab' : 'Disclaimers & limitation of liability'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Situs disediakan “sebagaimana adanya”. Sejauh diizinkan oleh hukum yang berlaku, kami tidak memberikan jaminan apa pun, dan tidak bertanggung jawab atas kerugian yang timbul dari penggunaan situs.'
                : 'The website is provided “as is”. To the extent permitted by applicable law, we make no warranties and are not liable for losses arising from use of the website.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Hukum yang berlaku' : 'Governing law'}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {lang === 'id'
                ? 'Syarat ini diatur oleh hukum Republik Indonesia.'
                : 'These terms are governed by the laws of the Republic of Indonesia.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">
              {lang === 'id' ? 'Kontak & informasi perusahaan' : 'Contact & company information'}
            </h2>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">{COMPANY.name}</div>
              <div className="mt-2 space-y-1">
                <div>
                  <span className="font-semibold">{lang === 'id' ? 'Alamat' : 'Address'}:</span>{' '}
                  {COMPANY.address}
                </div>
                <div>
                  <span className="font-semibold">{lang === 'id' ? 'Email' : 'Email'}:</span>{' '}
                  <a className="underline hover:text-slate-900" href={`mailto:${COMPANY.email}`}>
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
